import { useEffect, useRef } from 'react';
import { Button } from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toaster } from '@/components/ui/toaster';
import { Field } from '@/components/ui/field';
import { SimpleSelect } from '@/components/ui/simple-select';
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import useDialogCleanup from '@/lib/hooks/use-dialog-cleanup';
import api from '@/utils/api';
import { User, roleSelectItems, DailogProps } from '@/lib/types';

interface Props extends DailogProps {
  user: User;
  onSuccess(): void;
}

const formSchema = z.object({
  role_id: z.string().min(1, 'Role is required'),
});

type FormValues = z.infer<typeof formSchema>;

export default function ChangeRoleModal({ isOpen, onClose, user, onSuccess }: Props) {
  // This modal mounts already-open (rendered behind `roleModal.state &&`), which
  // can leave the body scroll/pointer lock behind on close — see use-dialog-cleanup.
  useDialogCleanup(isOpen);
  const { workspaceId = '0' } = useParams();

  const contentRef = useRef<HTMLDivElement>(null);

  const {
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
  });

  const onSubmit = async (data: FormValues) => {
    if (!user?.id) {
      throw new Error('User ID is undefined');
    }
    try {
      await api.post(`/users/role/workspace/change`, {
        user_id: user.id,
        role_id: parseInt(data.role_id, 10),
        workspace_id: parseInt(workspaceId, 10),
      });
      onClose();
      onSuccess();
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to change role',
      });
    }
  };

  useEffect(() => {
    if (isOpen && user) {
      reset({
        role_id: user.role_id.toString(),
      });
    }
  }, [isOpen, user, reset]);

  return (
    <DialogRoot lazyMount open={isOpen} onOpenChange={onClose}>
      <DialogBackdrop />
      <DialogTrigger />
      <DialogContent ref={contentRef}>
        <DialogCloseTrigger />
        <DialogHeader>
          <DialogTitle>Change Role</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <form id={'changeRole'} onSubmit={handleSubmit(onSubmit)}>
            <Field label='Role' invalid={!!errors.role_id} errorText={errors.role_id?.message}>
              <SimpleSelect
                items={roleSelectItems}
                value={watch('role_id')}
                onValueChange={(v) => setValue('role_id', v, { shouldValidate: true })}
                placeholder='Select role'
                contentRef={contentRef}
              />
            </Field>
          </form>
        </DialogBody>
        <DialogFooter>
          <Button colorPalette='brand' variant={'ghost'} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorPalette='brand'
            form={'changeRole'}
            type={'submit'}
            loading={isSubmitting}
            loadingText='Loading...'
          >
            Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
